import React, { useEffect, useState } from "react";
import { message, Upload } from "antd";
import uploadimg from "../../assets/uploadicon.svg";
import { DeleteUploadIcon, InfoIcon, UploadCsvIcon } from "../../assets/icon";

/**
 * Standalone CSV (or generic) file drag/drop — not tied to product PDF / Dragger redux flow.
 */
const CsvUploadDragger = ({
  accept = ".csv",
  maxSizeMB = 10,
  uploadText,
  supportedFormatsText,
  infoText,
  onFileSelected,
  onFileRemoved,
  initialFileName = "",
  className = "",
  showFileInside = true,
  multiple = false,
}) => {
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const formatsLabel =
    supportedFormatsText ||
    `Supported formats : ${String(accept || "")
      .split(",")
      .map((s) => s.trim().replace(/^\./, "").toUpperCase())
      .filter(Boolean)
      .join(", ")}`;


  useEffect(() => {
    const incomingName = String(initialFileName || "").trim();
    if (!incomingName) {
      setFileName("");
      return;
    }
    try {
      setFileName(decodeURIComponent(incomingName));
    } catch (error) {
      setFileName(incomingName);
    }
  }, [initialFileName]);

  const acceptList = String(accept || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const getDynamicFileTypeLabel = (file) => {
    const mimeType = String(file?.type || "").toLowerCase();
    const fileName = String(file?.name || "").toLowerCase();
    const accepted = acceptList.join(",");

    if (mimeType.startsWith("image/") || accepted.includes("image/")) return "Image";
    if (mimeType.startsWith("video/") || accepted.includes("video/")) return "Video";
    if (mimeType === "application/pdf" || fileName.endsWith(".pdf") || accepted.includes(".pdf")) return "PDF";
    if (mimeType.startsWith("audio/") || accepted.includes("audio/")) return "Audio";
    if (mimeType.includes("csv") || fileName.endsWith(".csv") || accepted.includes(".csv")) return "CSV";
    return "File";
  };

  const uploadProps = {
    name: "file",
    multiple,
    accept,
    showUploadList: false,
    async beforeUpload(file) {
      const max = Number(maxSizeMB || 10);
      if (file.size / 1024 / 1024 > max) {
        const fileTypeLabel = getDynamicFileTypeLabel(file);
        message.error(`${fileTypeLabel} file size exceeds ${max}MB limit!`);
        setFileName("");
        return Upload.LIST_IGNORE;
      }
      if (acceptList.length) {
        const name = String(file?.name || "").toLowerCase();
        if (!acceptList.some((ext) => name.endsWith(ext))) {
          message.error(`You can only upload ${acceptList.join(", ")} files!`);
          setFileName("");
          return Upload.LIST_IGNORE;
        }
      }
      try {
        setUploading(true);
        if (typeof onFileSelected === "function") {
          const uploadResult = await onFileSelected(file);
          if (uploadResult === false) {
            setFileName("");
            return Upload.LIST_IGNORE;
          }
        }
        setFileName(file?.name || "");
      } catch (error) {
        setFileName("");
        message.error(error?.message || "File upload failed");
        return Upload.LIST_IGNORE;
      } finally {
        setUploading(false);
      }
      return false;
    },
  };

  return (
    <div className={`table-padding tabbing btnPosition w-100 csvUploadContainer ${className}`.trim()}>
      {(!fileName || !showFileInside) ? (
        <Upload.Dragger {...uploadProps} disabled={uploading}>
          <p className="ant-upload-drag-icon">
            <img src={uploadimg} alt="" />
          </p>
          <p className="ant-upload-text">
            {uploading ? (
              <>Uploading...</>
            ) : (
              uploadText || (
                <>
                  <u>Click to upload</u> or drag and drop
                </>
              )
            )}
          </p>
        </Upload.Dragger>
      ) : null}

      {fileName && showFileInside && (
        <div className="ant-upload-list ant-upload-list-text d-flex flex-wrap w-100">
          <span className="ant-upload-list-item-container customCsvUploadlistContainer">
            <div className="d-flex gap-2 align-items-center file-icon">
              <div className="uploadCsvIconContainer">
                <UploadCsvIcon />
              </div>
              <div className="fw-bold fs-14  " style={{ color: "#162C4E", maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", }}>
                {fileName}
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="deleteCampapign bg-transparent border-0 p-0 "
                onClick={() => {
                  setFileName("");
                  if (typeof onFileRemoved === "function") onFileRemoved();
                }}
                aria-label="Remove uploaded file"
              >
                <DeleteUploadIcon />
              </button>
            </div>
          </span>
        </div>
      )}

      {(supportedFormatsText || accept || maxSizeMB) && (
        <div className="mt-2 d-flex justify-content-between textgrey fs-14 w-100 px-2">
          <div>{supportedFormatsText}</div>
          <div>Maximum file size : {Number(maxSizeMB || 10)} MB</div>
        </div>
      )}

      {!!infoText && (
        <div className="mt-3 d-flex align-items-start gap-2 text-muted">
          {/* <InfoCircleOutlined style={{ marginTop: 2 }} /> */}
          <InfoIcon />
          <div className="fs-16" >{infoText}</div>
        </div>
      )}
    </div>
  );
};

export default CsvUploadDragger;
